import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { type AccountCell, accountCell, countAiAccounts } from '../lib/fleet-work';
import type { UtilKey, UtilKeysStatus } from '../lib/fleetUtilization';
import {
  type KeyCapacityReadStatus,
  keyCapacityState,
  keyCapacityUsage,
} from '../lib/key-capacity';
import { orgHref } from '../lib/org';
import { localWallClock } from '../lib/time';
import { keyDisplayTitle, ProviderMark } from './KeyCard';
import { Panel } from './Panel';

// The Fleet page's AI accounts (#6788, prototype `workAndAccounts`): every AI
// account (a provider key, by its label) the machines sign work with, said as "AI
// accounts", never keys, subs or runners.
//
//   - three counts: connected (every account in the read, which serves non-revoked
//     keys only), in use (a claim in flight) and resting (out of quota until a
//     cooldown lapses). Resting wins over in use (`accountCell`), so they never
//     overlap;
//   - one cell per account: in use is the live accent, resting amber, the rest the
//     track;
//   - one row per account, its provider mark, label (else provider + model), priority
//     and live usage (#6864), in the server's order:
//     #6863's fold over the page's one `GET /v1/billing/capacity` read, matched by
//     `key_id`. "N of M running, K waiting"; a failed read says usage is unavailable
//     and a key the read does not list is not taking jobs, never 0; nothing while the
//     first read is in flight. Read-only: the control lives on `/account/keys`;
//   - one row per resting account, the same identity and priority, and when it is
//     back, as a viewer-local wall clock dated when it is not today;
//   - the line that says what resting means, or that every account rests and work
//     waits (`subs_exhausted`), or that there is no account yet;
//   - "Manage AI accounts", or "Connect an AI account" when there is none.
//
// Presenter: the container hands it the resolved utilization rows and the capacity
// read's status (`useKeyCapacityRead`, polled every 20 s like the utilization).

/** A cell's modifier: in use is a LIVE surface, the one accent (`accent-discipline`). */
const CELL_CLASS: Readonly<Record<AccountCell, string>> = {
  in_use: 'fleet-accounts__cell--live',
  resting: 'fleet-accounts__cell--resting',
  free: 'fleet-accounts__cell--free',
};

/** "Out of quota, back at …": a viewer-local wall clock, dated when not today. */
function backAt(iso: string): string {
  const { time, date } = localWallClock(iso);
  return date === null
    ? t('fleet.util.accounts_back_at', { time })
    : t('fleet.util.accounts_back_at_date', { time, date });
}

/** The explanatory line under the counts. */
function explainer(connected: number, resting: number, status: UtilKeysStatus): string {
  if (connected === 0) return t('fleet.util.accounts_none');
  if (status.subs_exhausted) return t('fleet.util.accounts_all_resting');
  return resting > 0
    ? t('fleet.util.accounts_some_resting')
    : t('fleet.util.accounts_none_resting');
}

export function AiAccountsPanel(props: {
  keys: readonly UtilKey[];
  status: UtilKeysStatus;
  /** The capacity view's read; folded per key by #6863's `keyCapacityState`. */
  capacity: KeyCapacityReadStatus;
}): JSX.Element {
  const usage = (key: UtilKey) => keyCapacityUsage(keyCapacityState(props.capacity, key.id));
  const counts = () => countAiAccounts(props.keys);
  const resting = () => props.keys.filter((key) => accountCell(key) === 'resting');
  return (
    <Panel class="fleet-accounts" title={t('fleet.util.accounts_heading')}>
      <p class="fleet-accounts__lede">{t('fleet.util.accounts_lede')}</p>
      <ul class="fleet-accounts__nums" aria-label={t('fleet.util.accounts_counts_aria')}>
        <li>
          <span class="fleet-accounts__num">{counts().connected}</span>{' '}
          {t('fleet.util.accounts_count_connected')}
        </li>
        <li>
          <span class="fleet-accounts__num">{counts().in_use}</span>{' '}
          {t('fleet.util.accounts_count_in_use')}
        </li>
        <li>
          <span class="fleet-accounts__num" data-resting={counts().resting > 0 ? '' : undefined}>
            {counts().resting}
          </span>{' '}
          {t('fleet.util.accounts_count_resting')}
        </li>
      </ul>
      <Show when={props.keys.length > 0}>
        <div
          class="fleet-accounts__cells"
          role="img"
          aria-label={t('fleet.util.accounts_cells_aria', {
            in_use: counts().in_use,
            resting: counts().resting,
            free: counts().connected - counts().in_use - counts().resting,
          })}
        >
          <For each={props.keys}>
            {(key) => <i class={`fleet-accounts__cell ${CELL_CLASS[accountCell(key)]}`} />}
          </For>
        </div>
      </Show>
      <Show when={props.keys.length > 0}>
        <ul class="fleet-accounts__keys">
          <For each={props.keys}>
            {(key) => (
              <li class="fleet-accounts__key">
                <span class="fleet-accounts__identity">
                  <ProviderMark provider={key.provider} />
                  <span class="fleet-accounts__details">
                    <span class="fleet-accounts__label">{keyDisplayTitle(key)}</span>
                    <span class="fleet-accounts__priority">
                      {t('account_keys.priority_value', { value: key.priority })}
                    </span>
                  </span>
                </span>
                <Show when={usage(key)}>
                  {(line) => <span class="fleet-accounts__usage">{line()}</span>}
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Show>
      <For each={resting()}>
        {(key) => (
          <div class="fleet-accounts__resting">
            <span class="fleet-accounts__identity">
              <ProviderMark provider={key.provider} />
              <span class="fleet-accounts__details">
                <span class="fleet-accounts__label">{keyDisplayTitle(key)}</span>
                <span class="fleet-accounts__priority">
                  {t('account_keys.priority_value', { value: key.priority })}
                </span>
              </span>
            </span>
            <span class="fleet-accounts__back">
              {key.cooldown_until_iso === null ? '' : backAt(key.cooldown_until_iso)}
            </span>
          </div>
        )}
      </For>
      <p class="fleet-accounts__explain">
        {explainer(counts().connected, counts().resting, props.status)}
      </p>
      <A class="fleet-accounts__manage" href={orgHref('/account/keys')}>
        {props.keys.length === 0
          ? t('fleet.util.accounts_connect')
          : t('fleet.util.accounts_manage')}
      </A>
    </Panel>
  );
}
